/**
 * AGRI-DASHBOARD-R4 — the single operational model of a hydraulic SITE.
 *
 * The Dashboard site cards and the Pompage page must agree on what an
 * installation is doing right now. This module is the ONLY interpreter of a
 * site's operational state, exactly as `sectorOperationalModel` is for sectors.
 *
 * Truthfulness rules (non-negotiable):
 *
 *   - A site with a canonical PUMP binding takes its state from the pump's
 *     relay contact — and ONLY once commissioning has identified which channel
 *     that is. `relay_channel` null/blank ⇒ NOT_COMMISSIONED. We never assume
 *     K1 (section 4): borrowing another contact's state would report a pump as
 *     running because an unrelated circuit is closed.
 *
 *   - A site with NO pump binding (Lézignan) may derive state from its valve
 *     controllers, but only from fresh, complete frames. Anything less is
 *     UNKNOWN (section 4).
 *
 *   - A relay contact proves the CONTACT, not the motor shaft (section 8). The
 *     model reports `stateEvidence` so the UI can never silently upgrade
 *     "circuit closed" into "motor proven running". Until a real current/motor
 *     feedback signal exists, `relay_contact` is the strongest evidence
 *     available, and commissioning is what certifies that the contact actually
 *     switches the motor.
 *
 *   - "Depuis quand" requires a PROVEN transition in history, through the same
 *     shared walk the sector model uses. Never the age of the last packet.
 *
 *   - An absent metric is absent. A real measured 0 stays 0.
 *
 * Pure helpers only — no React, no network, no side effects.
 */

import type { IoTNode, PumpBindingView } from './iotApi';
import {
  getNodeRelayChannels,
  getRelayChannelState,
  getRelayChannels,
  type RelayChannelState,
} from './liveSensorMetrics';
import {
  FRESHNESS_MS,
  deriveOperationalState,
  deriveRunSince,
  nodeTimestamp,
  type BinaryPointState,
  type OperationalState,
  type SectorPlacement,
  type TelemetryPoint,
} from './operationalOverview';
import {
  SECTOR_METRIC_DEFS,
  readSectorMetric,
  type SectorMetric,
} from './sectorOperationalModel';

/**
 * What the site's state is actually derived from.
 *
 *  - `relay_contact` — the pump's commissioned relay channel was observed.
 *  - `valve_states`  — no pump binding; derived from fresh valve controllers.
 *  - `none`          — nothing could prove a state.
 */
export type SiteStateEvidence = 'relay_contact' | 'valve_states' | 'none';

export interface SiteOperationalModel {
  siteName: string;

  /** Every canonical pump binding for this site. */
  pumps: PumpBindingView[];
  /** First pump retained for backwards-compatible single-pump surfaces. */
  pump: PumpBindingView | undefined;
  /** The pump's relay node, when it is present in the node list. */
  relayNode: IoTNode | undefined;
  /** K1..K4 contacts of the relay node — diagnostic only, never a farmer metric. */
  relayChannels: RelayChannelState[] | undefined;

  /**
   * True when commissioning has identified WHICH relay channel drives this
   * pump. Mirrors the backend gate (field_bindings.py: non-null, non-blank
   * `relay_channel`). False ⇒ the state is NOT_COMMISSIONED, never a guess.
   */
  relayChannelCommissioned: boolean;

  operationalState: OperationalState;
  stateEvidence: SiteStateEvidence;
  /** Duration in ms the site has held `operationalState`, or null if unproven. */
  stateSinceMs: number | null;
  /** Epoch ms of the freshest confirmation of the state SOURCE, or null. */
  lastConfirmationMs: number | null;

  /**
   * Air temperature/humidity read from the PUMP'S RELAY NODE ONLY.
   *
   * The relay module carries its own onboard air sensor (``air_temperature_c``
   * / ``air_humidity_percent``). These are deliberately NOT merged with a
   * site's `soil_air_ecth` ambient node — the two measure different physical
   * things, and conflation would let a soil-sector sensor masquerade as a
   * pumping-station reading.
   *
   * `freshness === 'absent'` means the relay metric is not ingested today; the
   * UI renders it as "Non remonté", never a fallback value from another node.
   */
  relayAirTemperature: SectorMetric;
  relayAirHumidity: SectorMetric;

  /** Freshest communication across every node attributed to this site. */
  lastCommunicationMs: number | null;
  /** Nodes canonically attributed to this site (sector-bound) plus its relay. */
  siteNodes: IoTNode[];
}

// ---------------------------------------------------------------------------
// Node → site attribution.
// ---------------------------------------------------------------------------

/**
 * Nodes whose canonical `sector_id` resolves to a sector OF THIS SITE.
 *
 * Same fail-closed rule as `nodesForSector`: a node with no sector id, or one
 * that does not resolve, belongs to no site. There is no farm-scoped fallback,
 * so a site never displays another site's air temperature.
 */
export function nodesForSite(
  nodes: IoTNode[],
  placement: Map<string, SectorPlacement>,
  siteName: string,
): IoTNode[] {
  return nodes.filter((node) => {
    const sectorId = node.sector_id;
    if (!sectorId) return false;
    return placement.get(sectorId)?.siteName === siteName;
  });
}

// ---------------------------------------------------------------------------
// Relay contact history → proven transition.
// ---------------------------------------------------------------------------

/**
 * Per-point relay contact state for one channel, UNKNOWN-preserving.
 *
 * Reads the history frame's `measures` directly — a history point is not a
 * node, and dressing it up as one would drag node-level model/root rules into
 * a place where they do not apply. An unrecognised channel yields UNKNOWN.
 */
const relayPointState = (
  point: TelemetryPoint,
  channel: string,
): BinaryPointState => {
  const normalized = channel.trim().toUpperCase();
  const contact = getRelayChannels(point.measures ?? undefined)
    .find((state) => state.channel === normalized)?.on ?? null;
  if (contact === true) return 'on';
  if (contact === false) return 'off';
  return 'unknown';
};

/**
 * How long the relay channel has held `on`/`off`, from a PROVEN transition in
 * its telemetry history. Returns null when the series cannot prove one.
 */
export function deriveRelayStateSince(
  series: TelemetryPoint[],
  channel: string,
  target: 'on' | 'off',
  now: number,
): number | null {
  return deriveRunSince(
    series,
    (point) => relayPointState(point, channel),
    (point) => point.ts,
    target,
    now,
  );
}

// ---------------------------------------------------------------------------
// State derivation.
// ---------------------------------------------------------------------------

/**
 * Mirrors the backend commissioning gate for a pump's relay channel
 * (backend/agrisafe/field_bindings.py:302-303). Deliberately identical: a
 * divergence would let the UI claim a state the backend considers unbound.
 */
export const isRelayChannelCommissioned = (
  pump: PumpBindingView | undefined,
): boolean =>
  pump !== undefined
  && typeof pump.relay_channel === 'string'
  && pump.relay_channel.trim().length > 0;

interface DerivedSiteState {
  state: OperationalState;
  evidence: SiteStateEvidence;
}

/**
 * Aggregate a site's valve controllers into one site state (no-pump sites).
 *
 * ANY controller proving an open valve ⇒ the site is irrigating. Otherwise
 * every controller must independently prove STOPPED for the site to read
 * stopped — one unknown controller is enough to make the whole site UNKNOWN,
 * because an unobserved controller may well be irrigating.
 */
function deriveStateFromControllers(
  controllers: IoTNode[],
  now: number,
): DerivedSiteState {
  if (controllers.length === 0) return { state: 'unknown', evidence: 'none' };

  const states = controllers.map((node) => deriveOperationalState(node, now));
  if (states.some((state) => state === 'active')) {
    return { state: 'active', evidence: 'valve_states' };
  }
  if (states.every((state) => state === 'stopped')) {
    return { state: 'stopped', evidence: 'valve_states' };
  }
  return { state: 'unknown', evidence: 'none' };
}

/**
 * Derive a site's operational state.
 *
 * Pump-bound sites read the commissioned relay contact; a stale relay node
 * cannot prove a present-tense state and degrades to UNKNOWN. Sites with no
 * pump fall back to their valve controllers.
 */
export function deriveSiteState(
  pump: PumpBindingView | undefined,
  relayNode: IoTNode | undefined,
  controllers: IoTNode[],
  now: number,
): DerivedSiteState {
  if (pump) {
    // Section 4: without a commissioned channel we say so, and stop.
    if (!isRelayChannelCommissioned(pump)) {
      return { state: 'not_commissioned', evidence: 'none' };
    }
    if (!relayNode) return { state: 'unknown', evidence: 'none' };

    const ts = nodeTimestamp(relayNode);
    if (ts === null || now - ts >= FRESHNESS_MS) {
      return { state: 'unknown', evidence: 'none' };
    }

    const contact = getRelayChannelState(relayNode, pump.relay_channel);
    if (contact === null) return { state: 'unknown', evidence: 'none' };
    return {
      state: contact ? 'active' : 'stopped',
      evidence: 'relay_contact',
    };
  }

  return deriveStateFromControllers(controllers, now);
}

/** Derive a site state from every semantic pump actuator assigned to it.
 * ACTIVE needs one fresh proven ON contact. STOPPED requires every actuator to
 * be freshly proven OFF; any remaining uncertainty fails closed. */
export function deriveMultiPumpSiteState(
  pumps: PumpBindingView[],
  nodes: IoTNode[],
  controllers: IoTNode[],
  now: number,
): { state: OperationalState; evidence: SiteStateEvidence } {
  if (pumps.length === 0) return deriveSiteState(undefined, undefined, controllers, now);

  const states = pumps.map((pump) => deriveSiteState(
    pump,
    nodes.find((node) => node.id === pump.relay_node_id),
    controllers,
    now,
  ));
  if (states.some((result) => result.state === 'active')) {
    return { state: 'active', evidence: 'relay_contact' };
  }
  if (states.every((result) => result.state === 'stopped')) {
    return { state: 'stopped', evidence: 'relay_contact' };
  }
  if (states.every((result) => result.state === 'not_commissioned')) {
    return { state: 'not_commissioned', evidence: 'none' };
  }
  return { state: 'unknown', evidence: 'none' };
}

// ---------------------------------------------------------------------------
// Model assembly.
// ---------------------------------------------------------------------------

const AIR_TEMPERATURE_DEF = SECTOR_METRIC_DEFS.find((def) => def.key === 'airTemperature')!;
const AIR_HUMIDITY_DEF = SECTOR_METRIC_DEFS.find((def) => def.key === 'airHumidity')!;

export interface BuildSiteModelInput {
  siteName: string;
  nodes: IoTNode[];
  placement: Map<string, SectorPlacement>;
  pumps: PumpBindingView[];
  /** Valve controllers of this site's sectors (used only when no pump exists). */
  controllers: IoTNode[];
  now: number;
  /** Optional relay telemetry history, keyed by node id, for "depuis quand". */
  historyByNodeId?: Map<string, TelemetryPoint[]>;
}

/**
 * Build the complete operational model of one hydraulic site.
 *
 * Shared by the Dashboard site cards and the Pompage page so the two surfaces
 * can never disagree about an installation's state, duration or provenance.
 */
export function buildSiteModel(input: BuildSiteModelInput): SiteOperationalModel {
  const { siteName, nodes, placement, pumps, controllers, now, historyByNodeId } = input;

  const sitePumps = pumps.filter((candidate) => candidate.site === siteName);
  const pump = sitePumps[0];
  const relayNodes = Array.from(new Map(
    sitePumps
      .map((candidate) => nodes.find((node) => node.id === candidate.relay_node_id))
      .filter((node): node is IoTNode => Boolean(node))
      .map((node) => [node.id, node]),
  ).values());
  const relayNode = relayNodes[0];

  const sectorBound = nodesForSite(nodes, placement, siteName);
  const siteNodes = [...sectorBound];
  for (const candidate of relayNodes) {
    if (!siteNodes.some((node) => node.id === candidate.id)) siteNodes.push(candidate);
  }

  const { state: operationalState, evidence: stateEvidence } = deriveMultiPumpSiteState(
    sitePumps, nodes, controllers, now,
  );

  // "Depuis quand" is derived from whichever source PROVED the state, so the
  // duration can never describe a different signal than the badge above it.
  let stateSinceMs: number | null = null;
  let lastConfirmationMs: number | null = null;

  if (
    stateEvidence === 'relay_contact'
    && relayNode
    && pump?.relay_channel
    && sitePumps.length === 1
  ) {
    lastConfirmationMs = nodeTimestamp(relayNode);
    const history = historyByNodeId?.get(relayNode.id) ?? [];
    stateSinceMs = deriveRelayStateSince(
      history,
      pump.relay_channel,
      operationalState === 'active' ? 'on' : 'off',
      now,
    );
  } else if (stateEvidence === 'relay_contact') {
    const stamps = relayNodes
      .map(nodeTimestamp)
      .filter((value): value is number => value !== null);
    lastConfirmationMs = stamps.length > 0 ? Math.max(...stamps) : null;
    stateSinceMs = null;
  } else if (stateEvidence === 'valve_states') {
    // The freshest controller is the site's confirmation of record.
    const stamps = controllers
      .map(nodeTimestamp)
      .filter((value): value is number => value !== null);
    lastConfirmationMs = stamps.length > 0 ? Math.max(...stamps) : null;
    // A site aggregates several controllers; only a single controller can prove
    // a transition, so a site-level "depuis" would be ambiguous. Left null —
    // the UI then shows "dernière confirmation", which is truthful.
  }

  // Relay air metrics come from the RELAY NODE ONLY (section 1 of R4.1).
  // `readSectorMetric` over the single-node array applies the shared freshness
  // rules: a stale relay timestamp → `freshness: 'stale'`, a missing relay
  // node or missing metric → `freshness: 'absent'`. No soil_air_ecth fallback.
  const relayAirTemperature = readSectorMetric(
    relayNode ? [relayNode] : [],
    AIR_TEMPERATURE_DEF,
    now,
  );
  const relayAirHumidity = readSectorMetric(
    relayNode ? [relayNode] : [],
    AIR_HUMIDITY_DEF,
    now,
  );

  const stamps = siteNodes
    .map(nodeTimestamp)
    .filter((value): value is number => value !== null);
  const relayTimestamp = relayNode ? nodeTimestamp(relayNode) : null;
  const relayFresh = relayTimestamp !== null && now - relayTimestamp < FRESHNESS_MS;

  return {
    siteName,
    pumps: sitePumps,
    pump,
    relayNode,
    relayChannels: relayNode && relayFresh ? getNodeRelayChannels(relayNode) : undefined,
    relayChannelCommissioned: sitePumps.length > 0 && sitePumps.every(isRelayChannelCommissioned),
    operationalState,
    stateEvidence,
    stateSinceMs,
    lastConfirmationMs,
    relayAirTemperature,
    relayAirHumidity,
    lastCommunicationMs: stamps.length > 0 ? Math.max(...stamps) : null,
    siteNodes,
  };
}
